import axios, { AxiosResponse, CancelToken, CancelTokenSource } from "axios"
import { UUIDType } from "uuid"
import { FinanciarFacturasCommandProps } from "./Commands/FinanciarFacturasCommand"
import { InsertFacturaOcrCommandProps } from "./Commands/InsertFacturaOcrCommand"
import { MarcaTransaccionManualmenteEnFacturaCommandProps } from "./Commands/MarcaTransaccionManualmenteEnFacturaCommand"
import { UpdateFacturaBorradorCommandProps } from "./Commands/UpdateFacturaBorradorCommand"
import { UpdateImpuestosFacturaCommandProps } from "./Commands/UpdateImpuestosFacturaCommand"
import { UpdateTagsFacturaCommandProps } from "./Commands/UpdateTagsFacturaCommand"
import { VerificarFacturasCommandProps } from "./Commands/VerificarFacturasCommand"
import { DocumentoComercialAsociadoReadingDtoProps } from "./DTOs/DocumentoComercialAsociadoReadingDto"
import { DocumentoComercialValidacionDtoProps } from "./DTOs/DocumentoComercialValidacionDto"
import { FacturaCreationDtoProps } from "./DTOs/FacturaCreationDto"
import { FacturaReadingDtoProps } from "./DTOs/FacturaReadingDto"
import { FacturaReadingFiltersDtoProps } from "./DTOs/FacturaReadingFiltersDto"
import { FacturaReadingSimpleDtoProps } from "./DTOs/FacturaReadingSimpleDto"
import { FicheroCreationDtoProps } from "./DTOs/FicheroCreationDto"
import { FicheroReadingDtoProps } from "./DTOs/FicheroReadingDto"
import { FlujoTesoreriaReadingDtoProps } from "./DTOs/FlujoTesoreriaReadingDto"
import { GetFacturaCarteraByFiltersDtoProps } from "./DTOs/GetFacturaCarteraByFiltersDto"
import { GetFacturasAceptablesDtoProps } from "./DTOs/GetFacturasAceptablesDto"
import { GetFacturasFinanciablesDtoProps } from "./DTOs/GetFacturasFinanciablesDto"
import { GetResumenContabilidadFacturaDto } from "./DTOs/GetResumenContabilidadFacturaDto"
import { UpdateContabilidadFacturaCommand } from "./DTOs/UpdateContabilidadFacturaCommandDto"
import { ValidacionDocumentoReadingDtoProps } from "./DTOs/ValidacionDocumentoReadingDto"
import {
  adapterFacturaCartera,
  adapterResumenFactura,
  facturaAceptableAdapter,
  facturaByFilterAdapter,
  facturaById,
  facturaByIdSimple,
  facturaDocAsociadosAdapter,
  facturaDocumentoPendienteValidacionAdapter,
  facturaFicherosAdjuntosAdapter,
  facturaFinanciableAdapter,
  getFlujoTesoreriaFacturaAdapter,
} from "./Factura.adapter"
import { FacturaModelProps } from "./Models/FacturaModel"
import { FacturaReadingFiltersDtoIPagedEnumerableProps } from "./Schemas/FacturaReadingFiltersDtoIPagedEnumerable"
import { GetFacturaCarteraByFiltersDtoIPagedEnumerable } from "./Schemas/GetFacturaCarteraByFiltersDtoIPagedEnumerable"
import { GetFacturasAceptablesDtoIPagedEnumerableProps } from "./Schemas/GetFacturasAceptablesDtoIPagedEnumerable"
import { GetFacturasConciliablesDtoIPagedEnumerableProps } from "./Schemas/GetFacturasConciliablesDtoIPagedEnumerable"
import { GetFacturasFinanciablesDtoIPagedEnumerableProps } from "./Schemas/GetFacturasFinanciablesDtoIPagedEnumerable"
import { EstadoFinanciacionEfectoType } from "./Types/EstadoFinanciacionEfecto"
import { EstadoOperacionType } from "./Types/EstadoOperacion"
import { EstadoRevisionDocumentoComercialType } from "./Types/EstadoRevisionDocumentoComercial"
import { EstadoVerificacionEfectoType } from "./Types/EstadoVerificacionEfecto"
import { EstadosCobroType } from "./Types/EstadosCobro"
import { EstadosPagoType } from "./Types/EstadosPago"
import { TipoDocumentoComercialType } from "./Types/TipoDocumentoComercial"
import { documentoComercialValidacionAdapter } from "./adapter_globals/DocumentoComercialValidacion.adapter"
import { entidadConciliableReadingDtoAdapter } from "./adapter_globals/EntidadConciliableReadingDto.adapter"
import { ConciliablesQueryParamsProps } from "./interfaces/ConciliablesQueryParams"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "./interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { SortingCriteria } from "./interfaces/SortingCriteria"

type EstadoVerificacionEfecto = 0 | 1 | 2
type SortingCriteriaOld = {
  sort?: "ASC" | "DESC"
  property?: string
}

export interface FacturasByFilterQueryParamsProps {
  EstadoFinanciacionEfecto?: Array<EstadoFinanciacionEfectoType>
  EstadoOperacion?: Array<EstadoOperacionType>
  EstadosCobro?: Array<EstadosCobroType>
  EstadosPago?: Array<EstadosPagoType>
  EmailLibrado?: string
  ClienteNif?: string
  ProveedorNif?: string
  EmpresaActivaIdUsuario?: string
  EstadoRevision?: Array<EstadoRevisionDocumentoComercialType>
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number
  ImporteUpperLimit?: number
  Keyword?: string
  LibradoExternoNif?: string
  LibradoId?: string
  LibradorExternoNif?: string
  LibradorId?: string
  MsgEncrypted?: string
  SortingCriteria: SortingCriteria
  MaxResultCount?: number
  SkipCount?: number
}

export async function getFacturasByFilter(
  queryParams: FacturasByFilterQueryParamsProps,
  cancelToken?: CancelToken
): Promise<FacturaReadingFiltersDtoIPagedEnumerableProps> {
  const { data } = await axios.get("/Factura/by-filters", {
    params: queryParams,
    cancelToken,
  })

  return {
    currentPage: data.currentPage,
    items: data.items?.map((factura: FacturaReadingFiltersDtoProps) =>
      facturaByFilterAdapter(factura)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Get Facturas Financiables
 */
type EstadoFinanciacionEfectoFinanciables = 1 | 2 | 3 | 4 | 5 | 6
type EstadoOperacionFinanciables =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
type EstadoPagoFinanciables = 0 | 1 | 2
type EstadoRevisionFinanciables = 0 | 1 | 2
export interface FinanciablesProps {
  divisa: string
  fechaEmision: string
  importe: number
  nif: string
  numero: string
  razonSocial: string
  id: string
}

export interface FinanciablesDataProps {
  currentPage: number
  items: FinanciablesProps[]
  totalCount: number
}
export interface FinanciablesQueryParamsProps {
  EmailLibrado?: string
  EmailLibrador?: string
  EmpresaActivaIdUsuario?: string
  EstadoFinanciacionEfecto?: EstadoFinanciacionEfectoFinanciables
  EstadoOperacion?: EstadoOperacionFinanciables
  EstadoPago?: EstadoPagoFinanciables
  EstadoRevision?: EstadoRevisionFinanciables[]
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number | string
  ImporteUpperLimit?: number | string
  LibradoId?: string
  LibradorId?: string
  MaxResultCount?: number
  MsgEncrypted?: string
  NifLibrado?: string
  NifLibrador?: string
  RazonSocialLibrado?: string
  RazonSocialLibrador?: string
  SerieNumero?: string
  SkipCount?: number
  SortingCriteria: SortingCriteriaOld
}
export async function getFacturasFinanciables(
  queryParams: FinanciablesQueryParamsProps
): Promise<GetFacturasFinanciablesDtoIPagedEnumerableProps> {
  const { data }: { data: GetFacturasFinanciablesDtoIPagedEnumerableProps } =
    await axios.get("/Factura/financiables", {
      params: queryParams,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((factura: GetFacturasFinanciablesDtoProps) =>
      facturaFinanciableAdapter(factura)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Get Facturas Aceptables
 */

export interface AceptablesQueryParamsProps {
  EmpresaId?: string
  Estados?: EstadoVerificacionEfectoType[]
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number | string
  ImporteUpperLimit?: number | string
  Keyword?: string
  MaxResultCount?: number | string
  SkipCount?: number | string
  SortingCriteria: SortingCriteriaOld
}

export async function getFacturasAceptables(
  queryParams: AceptablesQueryParamsProps,
  cancelToken?: CancelToken
): Promise<GetFacturasAceptablesDtoIPagedEnumerableProps> {
  const { data }: { data: GetFacturasAceptablesDtoIPagedEnumerableProps } =
    await axios.get("/Factura/aceptables/", {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data?.items?.map((factura: GetFacturasAceptablesDtoProps) =>
      facturaAceptableAdapter(factura)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Check if User has any Facturas
 * @returns {Boolean}
 */
export async function hasAnyFactura(
  queryParams?: EstadoVerificacionEfecto[]
): Promise<boolean> {
  const { data }: Record<"data", boolean> = await axios.get("/Factura/any", {
    params: queryParams,
  })
  return data
}

/**
 * @description Check if User has any Facturas
 * @returns {Boolean}
 */
export async function getUnsignedFacturas(
  queryParams?: DocumentoComercialValidacionDtoQueryParamsProps
): Promise<Array<DocumentoComercialValidacionDtoProps>> {
  const {
    data,
  }: Record<
    "data",
    Array<DocumentoComercialValidacionDtoProps>
  > = await axios.get("/Factura/pendientes-firmar", {
    params: queryParams,
  })
  return data.map((documento: DocumentoComercialValidacionDtoProps) =>
    documentoComercialValidacionAdapter(documento)
  )
}

export async function isFirstFactura(): Promise<boolean> {
  const { data }: Record<"data", boolean> = await axios.get(
    "/Factura/any-estado-verificacion-actualizado"
  )
  return data
}

export async function postFactura(documento: FacturaCreationDtoProps) {
  const resp = await axios.post("/Factura", documento)
  return resp
}

export async function postValidateFacturaPendiente(id: string): Promise<void> {
  await axios.post(`/Factura/${id}/validar`)
}

export async function getFacturaPdfById(id: string) {
  const { data, status } = await axios.get(`/Factura/${id}/pdf`, {
    responseType: "blob",
  })
  return { data, status }
}

export async function postFacturaPdf(documento: FacturaModelProps) {
  const { data } = await axios.post("/Factura/generate-pdf", documento, {
    responseType: "blob",
  })
  return data
}

export async function postFacturaOcr(
  queryBody: InsertFacturaOcrCommandProps
): Promise<UUIDType> {
  const { data: uuidResponse }: Record<"data", UUIDType> = await axios.post(
    "/Factura/ocr",
    queryBody
  )
  return uuidResponse
}

export async function validarCuentasContables(
  id: string,
  data: UpdateContabilidadFacturaCommand
): Promise<number> {
  const response = await axios.put(`Factura/${id}/contabilidad`, data)
  return response.status
}

export async function validarImpuestos(
  id: string,
  data: UpdateImpuestosFacturaCommandProps
): Promise<number> {
  const response = await axios.put(`Factura/${id}/impuestos`, data)
  return response.status
}

export async function getResumenValidacionImpuestos(
  id: string
): Promise<GetResumenContabilidadFacturaDto> {
  const { data } = await axios.get(`/Factura/${id}/resumen-contabilidad`)
  return adapterResumenFactura(data)
}

export interface FacturaCarteraQueryParamsProps {
  EmpresaContactoId?: string
  FechaLowerLimit?: string
  FechaUpperLimit?: string
  ImporteLowerLimit?: number
  ImporteUpperLimit?: number
  Keyword?: string
  TipoCartera?: number
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

export async function getFacturaCartera(
  queryParams: FacturaCarteraQueryParamsProps,
  cancelToken?: CancelToken
): Promise<GetFacturaCarteraByFiltersDtoIPagedEnumerable> {
  const { data }: { data: GetFacturaCarteraByFiltersDtoIPagedEnumerable } =
    await axios.get("/Factura/cartera/", {
      params: queryParams,
      cancelToken,
    })

  return {
    currentPage: data.currentPage,
    items: data.items?.map((cartera: GetFacturaCarteraByFiltersDtoProps) =>
      adapterFacturaCartera(cartera)
    ),
    totalCount: data.totalCount,
  }
}

export async function getFacturaCarteraImportePendiente(
  queryParams: FacturaCarteraQueryParamsProps,
  cancelToken?: CancelToken
): Promise<number> {
  const { data }: { data: number } = await axios.get(
    `/Factura/cartera/importe-pendiente/`,
    {
      params: queryParams,
      cancelToken,
    }
  )
  return data
}

export async function getFacturasByIdSimple(
  id: string
): Promise<FacturaReadingSimpleDtoProps> {
  const { data } = await axios.get(`/Factura/${id}/simple`)
  return facturaByIdSimple(data)
}

export async function getFacturaById(
  id: string
): Promise<FacturaReadingDtoProps> {
  const { data } = await axios.get(`/Factura/${id}`)
  return facturaById(data)
}

export async function getFacturaLastAvailableNumero(
  serie: string
): Promise<number> {
  const response = await axios.get(`/Factura/last-available-numero/${serie}`)
  return response.data
}

export async function getFacturaDocsAsociadosById(
  id: string
): Promise<DocumentoComercialAsociadoReadingDtoProps[]> {
  const { data } = await axios.get(`/Factura/${id}/documentos-asociados`)
  return data.map((element: DocumentoComercialAsociadoReadingDtoProps) =>
    facturaDocAsociadosAdapter(element)
  )
}

export async function getFacturaFicherosAdjuntosId(
  id: string
): Promise<FicheroReadingDtoProps[]> {
  const { data } = await axios.get(`/Factura/${id}/ficheros-adjuntos`)

  return data.map((element: FicheroReadingDtoProps) =>
    facturaFicherosAdjuntosAdapter(element)
  )
}

export async function deleteFacturaById(id: string) {
  const resp = await axios.delete(`/Factura/${id}`)
  return resp
}

// eslint-disable-next-line import/no-mutable-exports
export let facturasConciliablesCall: CancelTokenSource
export async function getFacturasConciliables(
  queryParams: ConciliablesQueryParamsProps
): Promise<GetFacturasConciliablesDtoIPagedEnumerableProps> {
  if (typeof facturasConciliablesCall !== typeof undefined) {
    facturasConciliablesCall.cancel("Previous Request Cancelled")
  }
  facturasConciliablesCall = axios.CancelToken.source()
  const {
    data,
  }: Record<"data", GetFacturasConciliablesDtoIPagedEnumerableProps> =
    await axios.get("/Factura/conciliables", {
      params: queryParams,
      cancelToken: facturasConciliablesCall.token,
    })
  return {
    currentPage: data.currentPage,
    items: data?.items?.map((item) =>
      entidadConciliableReadingDtoAdapter(item)
    ),
    totalCount: data.totalCount,
  }
}

export async function postImportarFactura(
  files: Array<File>
): Promise<AxiosResponse> {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append("FacturasE", file)
  })
  const response = await axios.post("/Factura/importar-facturae", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return response
}

export async function postFacturasExportExcel(queryParams: {
  tipoDocumentoComercial: TipoDocumentoComercialType
}): Promise<AxiosResponse<Blob>> {
  return axios.get("/Factura/exportar-excel", {
    params: queryParams,
    responseType: "blob",
  })
}

export async function getFlujoTesoreriaFactura(
  id: string
): Promise<FlujoTesoreriaReadingDtoProps[]> {
  const { data } = await axios.get(`/Factura/${id}/flujos-tesoreria`)

  return data.map((item: FlujoTesoreriaReadingDtoProps) =>
    getFlujoTesoreriaFacturaAdapter(item)
  )
}

export async function putFacturaMarcarTransaccionAutomatica(
  id: string,
  data: MarcaTransaccionManualmenteEnFacturaCommandProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `/Factura/${id}/marcar-transaccion-manualmente`,
    data
  )
  return response
}

export async function getFacturasDocumentoPendienteValidacionDocumentoPendienteValidacion(
  id: string
): Promise<ValidacionDocumentoReadingDtoProps> {
  const { data } = await axios.get(`/Factura/${id}/validacion`)
  return facturaDocumentoPendienteValidacionAdapter(data)
}

export async function deleteFacturaArichivoAdjunto(
  presupuestoID: string,
  fileID: string
) {
  return axios.delete(`/Factura/${presupuestoID}/fichero/${fileID}`)
}

export async function postFacturaAñadirArchivoAdjunto(
  presupuestoID: string,
  file: FicheroCreationDtoProps
) {
  return axios.post(`/Factura/${presupuestoID}/add-fichero-adjunto`, file)
}

export async function putActualizarFactura(
  id: string,
  document: UpdateFacturaBorradorCommandProps
): Promise<AxiosResponse<UpdateFacturaBorradorCommandProps>> {
  const response = await axios.put(`Factura/${id}/borrador`, document)
  return response
}

export async function postImportarExcel(
  tipoFactura: TipoDocumentoComercialType,
  fichero: Blob
): Promise<AxiosResponse> {
  const formData = new FormData()
  formData.append("fichero", fichero)

  const response = await axios.post("Factura/importar-excel", formData, {
    params: { tipoFactura },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return response
}

export async function putFacturaTags(
  queryParams: UpdateTagsFacturaCommandProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `Factura/${queryParams.id}/tags`,
    queryParams
  )
  return response
}

export async function putVerificarFactura(
  body: VerificarFacturasCommandProps
): Promise<AxiosResponse> {
  const response = await axios.put("/Factura/verificar", body)
  return response
}

export async function postFacturaFinanciar(
  facturasFinanciable: FinanciarFacturasCommandProps
): Promise<AxiosResponse> {
  const response = await axios.post("/Factura/financiar", facturasFinanciable)
  return response
}

export async function exportRemesasToExcel(
  queryParams: {
    FechaLowerLimit?: string
    FechaUpperLimit?: string
    TipoCartera?: number
  },
  cancelToken?: CancelToken
): Promise<AxiosResponse> {
  const res = await axios.get("/Factura/cartera/exportar-excel", {
    params: queryParams,
    responseType: "blob",
    cancelToken,
  })
  return res
}
